import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Daily intake log — one row per "I took this dose" tap. Covers both
// medications and supplements via `item_kind`. The hook fetches today's
// log on mount and exposes a memoised lookup helper plus toggle/log
// methods so the checklist UI can render and mutate per dose.
export function useIntakeLog({ date } = {}) {
  const { user } = useAuth()
  const today = useMemo(
    () => date || new Date().toLocaleDateString('en-CA'),
    [date],
  )
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('intake_log')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', today)
    if (err) setError(err.message)
    else setEntries(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, today])

  useEffect(() => { fetch() }, [fetch])

  // O(1) lookup for "did I take this scheduled dose today?". scheduledTime
  // must be the same 'HH:MM' string the parent's schedule_times array
  // contains; pass null for as-needed doses (which always allow a new log).
  const isTaken = useCallback(
    (itemKind, itemId, scheduledTime) => {
      if (scheduledTime == null) return false
      return entries.some(
        (e) =>
          e.item_kind === itemKind &&
          e.item_id === itemId &&
          e.scheduled_time === scheduledTime,
      )
    },
    [entries],
  )

  const logDose = async ({ itemKind, itemId, scheduledTime = null, notes = null }) => {
    if (!user) return false
    const { error: err } = await supabase.from('intake_log').insert({
      user_id: user.id,
      item_kind: itemKind,
      item_id: itemId,
      date: today,
      scheduled_time: scheduledTime,
      notes,
    })
    if (err) {
      // 23505 = unique_violation — silently ignore double-taps.
      if (err.code !== '23505') {
        setError(err.message)
        return false
      }
    }
    await fetch()
    return true
  }

  const unlogDose = async ({ itemKind, itemId, scheduledTime }) => {
    if (!user || scheduledTime == null) return false
    const { error: err } = await supabase
      .from('intake_log')
      .delete()
      .eq('user_id', user.id)
      .eq('item_kind', itemKind)
      .eq('item_id', itemId)
      .eq('date', today)
      .eq('scheduled_time', scheduledTime)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  const toggleDose = async (args) => {
    if (isTaken(args.itemKind, args.itemId, args.scheduledTime)) {
      return unlogDose(args)
    }
    return logDose(args)
  }

  return { entries, loading, error, isTaken, logDose, unlogDose, toggleDose, refetch: fetch }
}
